import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, FocusEvent, ReactNode, useRef } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import ConditionalWrapper from "./ConditionalWrapper";
import sanitizeInput, { Sanitizers } from "./sanitizeInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props<T> {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: T;
  id?: string;
  sanitize?: Sanitizers<T>;
  validators?: Validators<T>;
  onBlur?: (value: T) => void;
  onChange?: (value: T) => void;
  propertyName: string;
  excludeRow?: boolean;
  widthClassName?: string;
  capitalize?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const TextInput = <T extends number | string>(props: Props<T>) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, placeholder, prefix, suffix, info, tooltip } = useFieldData(props.fieldData, name);
  const isDirtyRef = useRef(Boolean(props.inputValue));
  const modelState = props.modelState;

  const sanitizeAndValidateField = (input: T) => {
    const parsedInput = props.capitalize && typeof input === "string" ? (input.toUpperCase() as T) : input;
    const sanitizedValue = sanitizeInput(parsedInput, props.sanitize);

    if (isDirtyRef.current && props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, sanitizedValue));
    }

    return sanitizedValue;
  };

  const onFieldBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value as T;

    if (!isDirtyRef.current && value) {
      isDirtyRef.current = true;
    }

    const validatedValue = sanitizeAndValidateField(value);

    if (!props.onBlur) {
      return;
    }

    e.target.value = validatedValue as string;

    props.onBlur(validatedValue);
  };

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const validatedValue = sanitizeAndValidateField(e.target.value as T);

    if (!props.onChange) {
      return;
    }

    e.target.value = validatedValue as string;

    props.onChange(validatedValue);
  };

  const id = props.id || fieldName;
  const errorId = `${id}-error`;
  const hasError = hasValidationMessage(modelState, props.propertyName);

  return (
    <ConditionalWrapper
      condition={!props.excludeRow}
      wrap={(children: ReactNode) => <div className="form-row">{children}</div>}
    >
      <div className={`form-item ${props.widthClassName}`}>
        <div className="form-input">
          {heading.value && (
            <div className="form-label">
              <label htmlFor={id}>{heading.value}</label>
              <Tooltip fieldName={fieldName} value={tooltip.value} />
            </div>
          )}
          {prefix.value && <div className="prefix">{prefix.value}</div>}
          <input
            required={props.required}
            placeholder={placeholder.value}
            type="text"
            // Set autoComplete to 'nope' instead of 'off',
            // because Chrome does not respect 'off'
            // https://bugs.chromium.org/p/chromium/issues/detail?id=370363#c7
            autoComplete="nope"
            name={fieldName}
            id={id}
            defaultValue={props.inputValue}
            onBlur={onFieldBlur}
            onChange={onFieldChange}
            aria-invalid={hasError}
            aria-errormessage={hasError ? errorId : undefined}
            disabled={props.disabled}
          />
          {suffix.value && <div className="suffix">{suffix.value}</div>}
        </div>
        {info.value && <span>{info.value}</span>}
      </div>
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </ConditionalWrapper>
  );
};

export default TextInput;
